import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Tr } from '@utils';
import { IVehicleQuestionCommonProps } from '@models';
import { Checkbox, Grid, ListItemText, MenuItem, Stack, Typography } from '@mui/material';
import { FormikNumber, FormikSlider, FormikTextfield } from '@components/common';
import { FormStepLayout, FormStepQuestion } from '@components/portal/common';
import { DaysOfUsage, DriverHabit, FleetVehicle, ParkingTimeSlot } from '@services/fleet-vehicle';

type FormFields = Partial<FleetVehicle>;
const translationPath = (key: string) => `vehicle-list.add-edit-vehicle.question-vehicle-usage.${key}`;
const getFormFieldValues = (values?: FormFields): FormFields => ({
	totalKmYear: values?.totalKmYear,
	averageKmDaily: values?.averageKmDaily,
	longestTripWeekly: values?.longestTripWeekly,
	driverHabit: values?.driverHabit,
	parkingTimeSlot: values?.parkingTimeSlot,
	parkingDurationMins: values?.parkingDurationMins || 6 * 60,
	daysOfUsage: values?.daysOfUsage || []
});
const getDriverHabitText = (inp: DriverHabit): ReactNode => {
	if (inp === DriverHabit.URBAN) {
		return <Tr.Portal path="vehicle-list.habit-urban" />;
	} else if (inp === DriverHabit.HIGHWAY) {
		return <Tr.Portal path="vehicle-list.habit-highway" />;
	} else if (inp === DriverHabit.COMBINED) {
		return <Tr.Portal path="vehicle-list.habit-combined" />;
	} else {
		return inp;
	}
};
const getParkingTimeSlotText = (inp: ParkingTimeSlot): ReactNode => {
	if (inp === ParkingTimeSlot.DAY) {
		return <Tr.Portal path="vehicle-list.parking-time-slot-day" />;
	} else if (inp === ParkingTimeSlot.NIGHT) {
		return <Tr.Portal path="vehicle-list.parking-time-slot-night" />;
	} else {
		return inp;
	}
};

export interface IQuestionVehicleUsageProps extends IVehicleQuestionCommonProps<FormFields> {}

export const QuestionVehicleUsage: FC<IQuestionVehicleUsageProps> = ({ values, onBack, onNext, ...props }) => {
	const { t } = useTranslation('portal');
	const [initialValues, setInitialValues] = useState<FormFields>(getFormFieldValues(values));
	useEffect(() => setInitialValues(getFormFieldValues(values)), [values]);

	const formik = useFormik<FormFields>({
		initialValues,
		enableReinitialize: true,
		validateOnChange: true,
		validateOnMount: true,
		validationSchema: yup.object({
			totalKmYear: yup.number().required(t('field-required')),
			averageKmDaily: yup.number().required(t('field-required')),
			longestTripWeekly: yup.number().required(t('field-required')),
			driverHabit: yup.string().required(t('field-required')),
			parkingTimeSlot: yup.string().required(t('field-required')),
			parkingDurationMins: yup.number().required(t('field-required'))
		}),
		onSubmit: (formValues, api) => {
			onNext?.(formValues);
			api.setSubmitting(false);
		}
	});
	const { submitForm, handleSubmit } = formik;
	const disabled = !formik.isValid || formik.isSubmitting;

	return (
		<FormStepLayout
			title={<Tr.Portal path={translationPath('title')} />}
			primaryButtonProps={{
				disabled,
				onClick: submitForm,
				'data-testid': 'add-edit-vehicle-next-button'
			}}
			secondaryButtonProps={{ onClick: onBack, 'data-testid': 'add-edit-vehicle-back-button' }}
			{...props}
		>
			<form onSubmit={handleSubmit}>
				<Stack direction="column" spacing={4}>
					<FormStepQuestion title={<Tr.Portal path={translationPath('first-question')} />}>
						<Grid container rowSpacing={5} columnSpacing={4}>
							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="totalKmYear"
									title={t(translationPath('total-km'))}
									placeholder={t(translationPath('total-km'))}
									required
									decimal={0}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="averageKmDaily"
									title={t(translationPath('avg-km'))}
									placeholder={t(translationPath('avg-km'))}
									required
									decimal={0}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="longestTripWeekly"
									title={t(translationPath('longest-trip'))}
									placeholder={t(translationPath('longest-trip'))}
									required
									decimal={0}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									select
									formikApi={formik}
									name="driverHabit"
									title={t(translationPath('habit'))}
									placeholder={t(translationPath('habit'))}
									required
								>
									{Object.values(DriverHabit).map((habit) => (
										<MenuItem key={`habit-${habit}`} value={habit}>
											{getDriverHabitText(habit)}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>
						</Grid>
					</FormStepQuestion>
					<FormStepQuestion title={<Tr.Portal path={translationPath('second-question')} />}>
						<Grid container rowSpacing={5} columnSpacing={4}>
							<Grid item xs={6}>
								<FormikTextfield
									select
									formikApi={formik}
									name="parkingTimeSlot"
									title={t(translationPath('time-slot'))}
									placeholder={t(translationPath('time-slot'))}
									required
								>
									{Object.values(ParkingTimeSlot).map((timeSlot) => (
										<MenuItem key={`time-slot-${timeSlot}`} value={timeSlot}>
											{getParkingTimeSlotText(timeSlot)}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>
							<Grid item xs={6}>
								<FormikSlider
									formikApi={formik}
									name="parkingDurationMins"
									title={t(translationPath('avg-parking-duration'))}
									required
									defaultValue={60}
									step={30}
									min={0}
									max={12 * 60}
									marks={Array.from(new Array(12)).map((_, i) => ({ value: (i + 1) * 60, label: `${i + 1}h` }))}
									valueLabelFormat={(value) => `${value / 60}h`}
									valueLabelDisplay="auto"
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									select
									formikApi={formik}
									name="daysOfUsage"
									title={t(translationPath('office-days'))}
									required
									SelectProps={{
										multiple: true,
										renderValue: (v) => {
											const days = v as DaysOfUsage[];
											if (days.length) {
												return days.map((day) => t(translationPath(`day-${day}`))).join(', ');
											} else {
												return (
													<Typography sx={{ color: 'grey.500' }}>
														{t(translationPath('office-days-placeholder'))}
													</Typography>
												);
											}
										}
									}}
								>
									{Object.values(DaysOfUsage).map((day) => (
										<MenuItem key={`days-of-usage-${day}`} value={day} dense>
											<Checkbox checked={formik.values.daysOfUsage?.includes(day)} size="small" disableRipple />
											<ListItemText primary={<Typography>{t(translationPath(`day-${day}`))}</Typography>} />
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>
						</Grid>
					</FormStepQuestion>
				</Stack>
			</form>
		</FormStepLayout>
	);
};
